import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import DeleteIcon from "../../assets/icons/svgs/delete.svg";
import { PrimaryBtn } from "../../components/BasicComponents";
import "./styles/CreateGiftCard.css";

import CustomDropdown from "../../components/CustomDropdown";
import instance from "../../axios";
import Spinner from "../../components/Loaders/Spinner";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import fieldValidate from "../../utils/fieldValidate";
import { getUserToken } from "../../utils/userAuthenticate";
import { gcValidityMonths, gcValidityYears } from "../../assets/currency"

import "../../localisation/index"
import GiftcardGallery from "../../components/GiftcardGallery";
import { sanitizeInput } from "@/hooks/useInputSanitization";
const CreateGiftCard = () => {
  const { t } = useTranslation()
  //values
  const [cardData, setCardData] = useState({
    title: "",
    description: "",
    terms: "",
    variants: [{ option1: "", price: "" }],
    expiry_month: "",
    expiry_year: "",
    expiration: true,
    show_barcode: false,
    allow_denomination: false,
    min_denomination: 1,
    max_denomination: 10000,
    self_purchase_quantity: 1,
    gift_quantity: 1,
  });
  const [activeValidity, setActiveValidity] = useState('months')

  // error
  const [isError, setIsError] = useState(null);

  // selected or current image
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState('')
  const navigate = useNavigate();

  // handle on change
  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    if (name === "terms" || name === "description") {
        setCardData((prev) => ({ ...prev, [name]: value }));
    } else if (name === "show_barcode") {
        setCardData((prev) => ({ ...prev, [name]: !prev.show_barcode }));
    } else if (name === "allow_denomination") {
        setCardData((prev) => ({ ...prev, [name]: !prev.allow_denomination }));
    } else if (name === "max_denomination" || name === "min_denomination") {
        setCardData((prev) => ({ ...prev, [name]: parseInt(value) || "" }));
    } else if (name === "self_purchase_quantity" || name === "gift_quantity") {
        if (/^\d{0,2}$/.test(value)) {
            const parsedValue = parseInt(value);
            if (parsedValue >= 1 && parsedValue <= 10) {
                setCardData((prev) => ({ ...prev, [name]: parsedValue || "" }));
                setIsError(null);
            } else if (value !== "") {
                setIsError(t("createGiftcard.quantityValidationError"));
            } else {
                setCardData((prev) => ({ ...prev, [name]: "" }));
                setIsError(null); 
            }
        } else {
            setIsError(t("createGiftcard.quantityValidationError"));
        }
    } else if (/^[a-zA-Z0-9.\s]*$/.test(value)) {
        setCardData((prev) => ({ ...prev, [name]: value }));
    }
};

  const handleVariant = (event) => {
    const index = event.target.id;
    const name = event.target.name;
    const value = event.target.value;
    if (name === "price") {
      if (/^(?!.*\..*\.)[0-9]*(\.[0-9]*)?$/.test(value)) {
        setCardData((prev) => {
          const updatedVariants = [...prev.variants];
          updatedVariants[index][name] = value;
          return { ...prev, variants: updatedVariants };
        });
      }
    } else if (/^[aA-zZ0-9.\s]*$/.test(value)) {
      setCardData((prev) => {
        const updatedVariants = [...prev.variants];
        updatedVariants[index][name] = value;
        return { ...prev, variants: updatedVariants };
      });
    }
  };

  // variant append and delete
  const handleAppend = () => {
    setCardData((prev) => ({
      ...prev,
      variants: [...prev.variants, { option1: "", price: "" }],
    }));
  };

  const handleDelete = (event) => {
    const index = event.target.id;

    setCardData((prev) => {
      const dt = { ...prev };
      const arr = dt.variants;
      arr.splice(index, 1);
      return dt;
    });
    console.log(index);
  };

  const handleSubmit = async () => {
    setIsError("")
    if(activeValidity==='months' && 'expiry_year' in cardData){
      delete cardData.expiry_year
    }else if(activeValidity==='years' && 'expiry_month' in cardData){
      delete cardData.expiry_month
    }

    if(cardData.allow_denomination){
      if(!cardData.min_denomination || !cardData.max_denomination){
        setIsError(t("createGiftcard.emptyDenomination"))
        setIsLoading(false);
        return;
      }else if(Number(cardData.min_denomination) >= Number(cardData.max_denomination)){
        setIsError(t("createGiftcard.denominationValidation"))
        setIsLoading(false);
        return;
      } 
    }

    setIsLoading(true);
    const url = "/giftcard/products/add";
    const headers = {
      Authorization: getUserToken(),
    };

    // field validation
    if (!fieldValidate(cardData.title, 3)) {
        setIsError(t("createGiftcard.titleError"));
        setIsLoading(false);
        return;
    } else if (!fieldValidate(cardData.description, 3)) {
        setIsError(t("createGiftcard.descriptionError"));
        setIsLoading(false);
        return;
    } else if (!fieldValidate(cardData.terms, 3)) {
        setIsError(t("createGiftcard.termsError"));
        setIsLoading(false);
        return;
    } else if (
        cardData.variants.length === 0 ||
        cardData.variants.some((item) => item.option1 === "" || item.price === "")
    ) {
        setIsError(t("createGiftcard.variantError"));
        setIsLoading(false);
        return;
    } else if (!selectedImage) {
        setIsError(t("createGiftcard.imageError"));
        setIsLoading(false);
        return;
    } else if (
        (activeValidity === "months" && !cardData.expiry_month) ||
        (activeValidity === "years" && !cardData.expiry_year)
    ) {
        setIsError(t("createGiftcard.validityError"));
        setIsLoading(false);
        return;
    } else if (
        !cardData.self_purchase_quantity ||
        !cardData.gift_quantity
    ) {
        setIsError(t("createGiftcard.quantityError"));
        setIsLoading(false);
        return;
    } else {
        setIsError(null);
    }

    // post request
    try {
        const res = await instance.post(
            url,
            {
                title: cardData.title.trim(),
                description: cardData.description.trim(),
                published: "true",
                variants: cardData.variants,
                images: [{ attachment: selectedImage.split(",")[1] }],
                terms: cardData.terms,
                expiration: cardData.expiration,
                ...(activeValidity === "months" && { expiry_month: cardData.expiry_month }),
                ...(activeValidity === "years" && { expiry_year: cardData.expiry_year }),
                show_barcode: cardData.show_barcode,
                allow_denomination: cardData.allow_denomination,
                ...(cardData.allow_denomination && { min_denomination: Number(cardData.min_denomination) }),
                ...(cardData.allow_denomination && { max_denomination: Number(cardData.max_denomination) }),
                self_purchase_quantity: Number(cardData.self_purchase_quantity),
                gift_quantity: Number(cardData.gift_quantity),
            },
            { headers }
        );
        const resData = res.data;

        if (resData.code === 200) {
            alert(resData.message);
            navigate("/my-gift-card");
        } else {
            alert(resData.message);
        }
      
    } catch (e) {
        alert(t("createGiftcard.error"));
    } finally {
        setIsLoading(false);
    }
};

  return (
    <div className="gift-card__container component">
      <div className="component-primary-heading">{t("dashboardMenu.publishGc")}</div>

      <div className="section-box-container component">
        {isLoading &&
          createPortal(<Spinner />, document.getElementById("portal"))
        }
        <div className="gift-card__card-details">
          <GiftcardGallery setSelectedImage={setSelectedImage} selectedImage={selectedImage} />

          <div className="gift-card__card-data">
            <label className="gift-card__label">
              {t("commonTexts.title")} <span className="mandatory">*</span>
            </label>
            <input
              type="text"
              className="gift-card__input"
              name="title"
              value={cardData.title}
              onInput={(e) => { setCardData((prev) => ({ ...prev, [e.target.name]: sanitizeInput(e.target.value, "giftcardTitle") })) }}
            />
            <label className="gift-card__label">
              {t("commonTexts.description")} <span className="mandatory">*</span>
            </label>
            <textarea
              type="text"
              className="gift-card__input"
              name="description"
              rows={3}
              value={cardData.description}
              onInput={(e) => {
                const sanitizedValue = sanitizeInput(e.target.value, "textarea");
                setCardData((prev) => ({ ...prev, [e.target.name]: sanitizedValue }));
              }}
            />
            <label className="gift-card__label">
              {t("commonTexts.terms")} <span className="mandatory">*</span>
            </label>
            <textarea
              type="text"
              className="gift-card__input"
              name="terms"
              rows={3}
              value={cardData.terms}
              onInput={(e) => { setCardData((prev) => ({ ...prev, [e.target.name]: sanitizeInput(e.target.value, "textarea") })) }}
            />
            
            <div className="gift-card__quantity-container">
              <div className="gift-card__label">
                {t("createGiftcard.GcPurchaseQuantity")} <span className="mandatory">*</span>
              </div>
              <div className="gift-card__quantity-inputs">
                <label>
                  {t("createGiftcard.buyForSelfQuantity")}
                  <input
                    type="number"
                    className="gift-card__input"
                    name="self_purchase_quantity"
                    value={cardData.self_purchase_quantity}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
                          e.preventDefault();
                      }
                    }}
                  />
                </label>
                <label>
                  {t("createGiftcard.sendAsGiftQuantity")}
                  <input
                    type="number"
                    className="gift-card__input"
                    name="gift_quantity"
                    value={cardData.gift_quantity} 
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
                          e.preventDefault();
                      }
                    }}
                  />
                </label>
              </div>
            </div>
            
            <div className="barcode-checkbox">
              <input type="checkbox" name="show_barcode" onChange={handleChange} checked={cardData.show_barcode} />
              <label>{t("createGiftcard.sendBarcode")}</label>
            </div>

            <div className="variable-denomination-container">
              <div>
                <input type="checkbox" name="allow_denomination" checked={cardData.allow_denomination} onChange={handleChange} />
                <label>{t("createGiftcard.enableDenomination")}</label>
              </div>
              {
                cardData.allow_denomination && (
                  <div className="variable-denomination-label">
                    <label>
                      {t("createGiftcard.minValue")}
                      <input type="number" name="min_denomination" value={cardData.min_denomination} onChange={handleChange} />
                    </label>
                    <label>
                      {t("createGiftcard.maxValue")}
                      <input type="number" name="max_denomination" value={cardData.max_denomination} onChange={handleChange} />
                    </label>
                  </div>   
                )
              }
            </div>

            <div className="gift-card__label">
              {t("createGiftcard.validity")} <span className="mandatory">*</span>
            </div>

            <div className="gift-card__validity-radio">
              <label>
                <input type="radio" checked={activeValidity === 'months'} name="validity" onChange={() => {
                  setCardData((prev) => ({ ...prev, expiration: true }))
                  setActiveValidity('months')
                }} />
                {t("createGiftcard.months")}
              </label>
              <label>
                <input type="radio" checked={activeValidity === 'years'} name="validity" onChange={() => {
                  setCardData((prev) => ({ ...prev, expiration: true }))
                  setActiveValidity('years')
                }} />
                {t("createGiftcard.years")}
              </label>
              <label>
                <input type="radio" checked={activeValidity === 'no_expiry'} name="validity" onChange={() => {
                  setCardData((prev) => ({ ...prev, expiration: false }))
                  setActiveValidity('no_expiry')
                }} />
                {t("createGiftcard.noExpiry")}
              </label>
            </div>
            <div className="gift-card__validity">
              {
                activeValidity === 'months' && (
                  <CustomDropdown
                    options={gcValidityMonths}
                    keyField="expiry_month"
                    value={cardData?.expiry_month}
                    setvalue={setCardData}
                  />
                )
              }
              {
                activeValidity === 'years' && (
                  <CustomDropdown
                    options={gcValidityYears}
                    keyField="expiry_year"
                    value={cardData?.expiry_year}
                    setvalue={setCardData}
                  />
                )
              }
            </div>
          </div>
        </div>

        <div className="gift-card__variant-data">
          <div className="gift-card__variant-grid-row">
            <label className="gift-card__label">
              {t("createGiftcard.variantName")} <span className="mandatory">*</span>
            </label>
            <label className="gift-card__label">
              {t("createGiftcard.denomination")} <span className="mandatory">*</span>
            </label>
            <label></label>
          </div>

          {cardData.variants.map((item, index) => (
            <div className="gift-card__variant-grid-row" key={index}>
              <input
                type="text"
                className="gift-card__variant-input-title gift-card__input"
                id={index}
                name="option1"
                value={item.option1}
                onChange={handleVariant}
                onWheel={(e) => e.target.blur()}
              />
              <input
                type="text"
                className="gift-card__variant-input-price gift-card__input"
                id={index}
                name="price"
                value={item.price}
                onChange={handleVariant}
                onWheel={(e) => e.target.blur()}
              />
              <img src={DeleteIcon} alt="" id={index} onClick={handleDelete} />
            </div>
          ))}
        </div>
        <div className="gift-card__append-variant" onClick={handleAppend}>
          {t("createGiftcard.addVariant")}
          <span>
            <FaPlus />
          </span>
        </div>

        {/* error */}
        {isError && <div className="gift-card__error">{isError}</div>}

        <PrimaryBtn $primary width={"230px"} onClick={handleSubmit}>
          {t("createGiftcard.publicGc")}
        </PrimaryBtn>
      </div>
    </div>
  );
};

export default CreateGiftCard;
